import React, { useMemo } from 'react';
import { Alert, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { normalizeApiErrorParts } from '../../utils/formatApiError';

const { Text } = Typography;

/**
 * Shared list/page error callout (403 policy, load failures, etc.).
 * Dedupes repeated "Missing required policy: X" lines and stacks cleanly on narrow screens.
 *
 * @param {unknown} error - string | string[] | axios-like | null
 * @param {string} [title]
 * @param {boolean} [closable]
 * @param {() => void} [onClose]
 * @param {React.CSSProperties} [style]
 */
export default function ApiErrorAlert({
  error,
  title,
  closable = false,
  onClose,
  action,
  style,
}) {
  const { t } = useTranslation();
  const parts = useMemo(() => normalizeApiErrorParts(error, t), [error, t]);

  if (!parts.length) return null;

  const policies = parts.filter((p) => /^Missing required policy:/i.test(p));
  const other = parts.filter((p) => !/^Missing required policy:/i.test(p));

  return (
    <Alert
      type="error"
      showIcon
      closable={closable}
      onClose={onClose}
      action={action}
      message={title ?? t('Error loading data')}
      description={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            minWidth: 0,
            wordBreak: 'break-word',
          }}
        >
          {other.map((line) => (
            <Text key={line} style={{ display: 'block' }}>
              {line}
            </Text>
          ))}
          {policies.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                marginTop: other.length ? 2 : 0,
              }}
            >
              {policies.map((line) => {
                const name = line.replace(/^Missing required policy:\s*/i, '');
                return (
                  <Text
                    key={line}
                    code
                    style={{
                      fontSize: 12,
                      margin: 0,
                      maxWidth: '100%',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {name}
                  </Text>
                );
              })}
            </div>
          )}
        </div>
      }
      style={{ marginBottom: 16, width: '100%', ...style }}
    />
  );
}
